import {
  createMouseInput,
  type MouseInputAdapter,
  type MouseRowViewportBounds,
  type SelectionUiInteraction,
  type SharedEventLoop,
} from '@databases/selections';
import { type DatabaseAdapter } from '@databases/selections-api-types';

import { type InputCapture } from '@canvas/input';

import {
  SelectionFeatureKeys,
  type SelectionInputFeature,
} from '../../features/types.ts';

import { createTableMouseInputAdapter } from './mouse-input-adapters/create-table-mouse-input-adapter.ts';

export interface MouseInputFeatureDeps {
  inputCapture: InputCapture;
  sharedLoop: SharedEventLoop;
  databaseAdapter: DatabaseAdapter;
  /**
   * Same contract as `FeatureDeps.onSelectionUiInteraction` in mode config (pointer telemetry).
   */
  onSelectionUiInteraction?: (event: SelectionUiInteraction) => void;
}

export function createMouseInputFeature(
  deps: MouseInputFeatureDeps,
  adapter: MouseInputAdapter = createTableMouseInputAdapter(),
): SelectionInputFeature {
  const viewportBounds: MouseRowViewportBounds = {
    getMaxVisibleRowIndex() {
      return Math.max(0, deps.databaseAdapter.getVisibleEntryCount() - 1);
    },
  };

  return {
    key: SelectionFeatureKeys.MOUSE_INPUT,
    type: 'input',
    factory: (machine) =>
      createMouseInput(machine, deps.inputCapture, {
        adapter,
        sharedLoop: deps.sharedLoop,
        viewportBounds,
        getVisibleEntryCount: () => deps.databaseAdapter.getVisibleEntryCount(),
        onSelectionUiInteraction: deps.onSelectionUiInteraction,
      }),
  };
}
